import { useTranslation } from '../i18n'
import type { Dog, Device } from '../lib/types'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'
import { PageHeader } from './atoms'

function MetaStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: COLORS.outline,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{value}</div>
    </div>
  )
}

function DogCard({ dog, devices }: { dog: Dog; devices: Device[] }) {
  const { t } = useTranslation('dogs')
  const device = devices.find((d) => d.id === dog.deviceId)
  return (
    <div
      className="wh-card"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            background: dog.color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="dog" size={28} />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: COLORS.fontDisplay,
              fontSize: 22,
              fontWeight: 500,
            }}
          >
            {dog.name}
          </div>
          <div style={{ fontSize: 13, color: COLORS.outline }}>{dog.breed}</div>
        </div>
        <span className={`wh-chip ${dog.status === 'tracking' ? '' : 'muted'}`}>
          {dog.status === 'tracking' ? t('status.active') : t('status.resting')}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        <MetaStat label={t('stat.age')} value={t('stat.ageValue', { count: dog.age })} />
        <MetaStat label={t('stat.gender')} value={dog.gender} />
        <MetaStat label={t('stat.team')} value={dog.team.split(' ')[0]} />
      </div>
      <div
        style={{
          fontSize: 12,
          padding: '10px 12px',
          background: COLORS.surface,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Icon name="device" size={14} />
        {device ? (
          <span>
            {t('pairing.pairedWith')} <b>{device.name}</b>
          </span>
        ) : (
          <span style={{ color: COLORS.outline }}>{t('pairing.noDevice')}</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="wh-btn wh-btn-outline" style={{ flex: 1 }}>
          <Icon name="edit" size={16} />
          {t('edit')}
        </button>
        <button className="wh-btn-icon">
          <Icon name="more" size={18} />
        </button>
      </div>
    </div>
  )
}

export interface DogsPageProps {
  dogs: Dog[]
  devices: Device[]
}

export function DogsPage({ dogs, devices }: DogsPageProps) {
  const { t } = useTranslation('dogs')
  return (
    <div>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle', { count: dogs.length })}
        actions={
          <button className="wh-btn wh-btn-primary">
            <Icon name="plus" size={16} />
            {t('registerDog')}
          </button>
        }
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {dogs.map((d) => (
          <DogCard key={d.id} dog={d} devices={devices} />
        ))}
      </div>
    </div>
  )
}
